import { BUTTON, type InputBits } from "../sim";

/**
 * Стандартная раскладка Gamepad API (W3C «standard»), DualSense в Chrome и Edge:
 * 0 ✕ · 1 ○ · 2 □ · 3 △ · 4 L1 · 5 R1 · 6 L2 · 7 R2 · 9 Options · 12–15 крестовина.
 */
export const PAD = {
  cross: 0,
  circle: 1,
  square: 2,
  triangle: 3,
  l1: 4,
  r1: 5,
  l2: 6,
  r2: 7,
  share: 8,
  options: 9,
  touchpad: 17,
  up: 12,
  down: 13,
  left: 14,
  right: 15,
} as const;

/**
 * DualSense на Linux, когда Chrome не узнал его и отдаёт сырую раскладку (порядок кнопок evdev, драйвер
 * hid-playstation): △ — 2, □ — 3, Options — 9, курки — ещё и оси 2 и 5 (в покое −1), крестовина — оси 6 и 7.
 * Джон 04.11.2026: «с ботом перестал работать джойстик» — в такой раскладке крестовина кнопками 12–15 не приходит.
 */
const SONY_RAW = { ...PAD, square: 3, triangle: 2 } as const;
const HAT_X = 6;
const HAT_Y = 7;

const STICK_DEADZONE = 0.5;

const pressed = (pad: Gamepad, index: number): boolean => pad.buttons[index]?.pressed ?? false;

/** Сырая раскладка Sony: не «standard», производитель 054c в имени, крестовина — осями. */
export const isSonyRaw = (pad: Gamepad): boolean => pad.mapping !== "standard" && /054c/i.test(pad.id);

export function padBits(pad: Gamepad): InputBits {
  const raw = isSonyRaw(pad);
  const layout = raw ? SONY_RAW : PAD;
  const x = pad.axes[0] ?? 0;
  const y = pad.axes[1] ?? 0;
  // крестовина: кнопки 12–15 в стандартной раскладке, оси шляпки в сырой
  const hatX = raw ? (pad.axes[HAT_X] ?? 0) : 0;
  const hatY = raw ? (pad.axes[HAT_Y] ?? 0) : 0;
  const dpad = (index: number): boolean => !raw && pressed(pad, index);
  let bits = 0;
  if (dpad(PAD.up) || y < -STICK_DEADZONE || hatY < -STICK_DEADZONE) bits |= BUTTON.up;
  if (dpad(PAD.down) || y > STICK_DEADZONE || hatY > STICK_DEADZONE) bits |= BUTTON.down;
  if (dpad(PAD.left) || x < -STICK_DEADZONE || hatX < -STICK_DEADZONE) bits |= BUTTON.left;
  if (dpad(PAD.right) || x > STICK_DEADZONE || hatX > STICK_DEADZONE) bits |= BUTTON.right;
  if (pressed(pad, layout.square)) bits |= BUTTON.lightPunch;
  if (pressed(pad, layout.triangle)) bits |= BUTTON.heavyPunch;
  if (pressed(pad, layout.cross)) bits |= BUTTON.lightKick;
  if (pressed(pad, layout.circle)) bits |= BUTTON.heavyKick;
  // R2 — блок (как в MK11, Джон 04.11.2026, 23:27) и второй курок суперприёма; R1 — под предметы арены (позже)
  if (pressed(pad, layout.r2)) bits |= BUTTON.block | BUTTON.enhance;
  if (pressed(pad, layout.l2)) bits |= BUTTON.super;
  // захват — L1 (Джон 04.11.2026, 23:26)
  if (pressed(pad, layout.l1)) bits |= BUTTON.grab;
  // издёвка — нажатие на тачпад (в Chrome — кнопка 17), запасная — Create (8)
  if (pressed(pad, PAD.touchpad) || (!raw && pressed(pad, PAD.share))) bits |= BUTTON.taunt;
  return bits;
}

export const padConfirm = (pad: Gamepad): boolean => pressed(pad, PAD.cross);
export const padPause = (pad: Gamepad): boolean => pressed(pad, PAD.options);

/** Вибрация, где браузер умеет (Chrome, Edge); где нет — молча ничего. */
export function padRumble(pad: Gamepad, strong: number, weak: number, duration: number): void {
  const actuator = pad.vibrationActuator as GamepadHapticActuator | null | undefined;
  if (!actuator) return;
  actuator.playEffect("dual-rumble", { duration, strongMagnitude: strong, weakMagnitude: weak }).catch(() => undefined);
}
